import { Clear } from '@mui/icons-material';
import {
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  SxProps,
  TextField,
} from '@mui/material';
import { DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { Calendar } from 'asset';
import { Moment } from 'moment';
import { Fragment, useState } from 'react';
import { useController } from 'react-hook-form';

interface DatePickerFieldProps {
  title?: string;
  name: string;
  form?: any;
  rules?: any;
  inputFormat?: string;
  readOnly?: boolean;
  disabled?: boolean;
  isClearable?: boolean;
  value?: Moment | any;
  onChange?: (value: Moment | any) => void;
  minDate?: Moment;
  maxDate?: Moment;
  errorsName?: string;
  iconColor?: string;
  sx?: SxProps;
}

const DateTimePicker = ({
  title,
  value,
  form,
  name,
  rules,
  inputFormat,
  errorsName,
  iconColor,
  disabled,
  readOnly,
  isClearable,
  onChange,
  sx,
  ...rest
}: DatePickerFieldProps) => {
  const { control } = form || { control: null };

  const {
    field: {
      onChange: onChangeCrl,
      onBlur: onBlurCrl,
      value: valueCrl,
      ref: refCrl,
    },
    formState: { errors },
    // eslint-disable-next-line react-hooks/rules-of-hooks
  } = control
    ? // eslint-disable-next-line react-hooks/rules-of-hooks
      useController({ name, control, rules })
    : {
        field: { onChange, value, onBlur: undefined, ref: undefined },
        formState: { errors: {} },
      };

  const [openDate, setOpenDate] = useState<boolean>(false);

  return (
    <Fragment>
      {!!title && (
        <InputLabel
          sx={{
            fontWeight: 500,
            fontSize: '15px',
            lineHeight: '100%',
            color: '#000',
            marginBottom: '5px',
          }}
        >
          {title}
        </InputLabel>
      )}
      <LocalizationProvider dateAdapter={AdapterMoment}>
        <DesktopDatePicker
          {...rest}
          open={openDate}
          inputRef={refCrl}
          value={valueCrl ?? null}
          inputFormat={inputFormat ?? 'DD MMM yyyy'}
          disableMaskedInput
          readOnly={readOnly || disabled}
          onChange={(value) => onChangeCrl?.(value)}
          onOpen={() => setOpenDate(true)}
          onClose={() => setOpenDate(false)}
          renderInput={(params) => (
            <TextField
              {...params}
              disabled={disabled}
              onBlur={onBlurCrl}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="start" sx={{ mr: 0 }}>
                    <IconButton
                      disabled={disabled}
                      onClick={() => setOpenDate(true)}
                      className="button-calendar"
                      sx={{
                        '&:hover': {
                          bgcolor: 'transparent',
                        },
                        '& .MuiTouchRipple-root': {
                          display: 'none',
                        },
                        position: 'absolute',
                        top: -5,
                        left: -16,
                        borderRadius: '0px',
                        cursor: 'pointer',
                        width: '230px',
                      }}
                    >
                      <Calendar color={iconColor ?? 'transparent'} />
                    </IconButton>
                    {!!valueCrl && !readOnly && !disabled && isClearable && (
                      <IconButton
                        sx={{ p: 0.5, m: -0.5 }}
                        onClick={() => onChangeCrl?.(null)}
                      >
                        <Clear />
                      </IconButton>
                    )}
                  </InputAdornment>
                ),
              }}
              sx={{
                background: disabled ? '#eaeaea' : '#fff',
                borderRadius: '5px',
                height: '38px',
                p: '16px',
                display: 'flex',
                justifyContent: 'center',
                border: '1px solid #D5D5D5',
                '& >.MuiInput-root>.MuiInput-input': {
                  fontWeight: 400,
                  fontSize: '13px',
                  lineHeight: '100%',
                  textTransform: !valueCrl ? 'uppercase' : 'none',
                },
                '& >.MuiInput-root:before': { display: 'none' },
                '& >.MuiInput-root:after': { display: 'none' },
              }}
              variant="standard"
              fullWidth
              InputLabelProps={{
                shrink: true,
              }}
            />
          )}
        />
      </LocalizationProvider>

      {!!(errors as any)[name] ? (
        <FormHelperText
          error
          sx={{
            fontSize: '14px',
            letterSpacing: '0.045em',
            color: 'white.light',
          }}
        >
          {(errors as any)?.[name]?.message || ''}
        </FormHelperText>
      ) : (
        <FormHelperText
          error
          sx={{
            fontSize: '14px',
            letterSpacing: '0.045em',
            color: 'white.light',
          }}
        >
          {errorsName || ''}
        </FormHelperText>
      )}
    </Fragment>
  );
};

export default DateTimePicker;
